// src/components/cms/RichHtmlEditor.jsx
import React, { useEffect } from "react";
import { Box, IconButton, Tooltip, Divider } from "@mui/material";
import {
  FormatBold, FormatItalic, FormatUnderlined,
  FormatListBulleted, FormatListNumbered,
  Link as LinkIcon, LinkOff as LinkOffIcon,
  Undo, Redo, Clear as ClearIcon,
} from "@mui/icons-material";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import LinkExt from "@tiptap/extension-link";

export default function RichHtmlEditor({ value, onChange, minHeight = 240 }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      LinkExt.configure({ openOnClick: true, autolink: true, linkOnPaste: true }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: { attributes: { class: "tiptap-editor" } },
  });

  useEffect(() => {
    if (!editor) return;
    const html = editor.getHTML();
    if (typeof value === "string" && value !== html) editor.commands.setContent(value, false);
  }, [value, editor]);

  const setOrUnsetLink = () => {
    if (!editor) return;
    if (editor.isActive("link")) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const url = window.prompt("Enter URL");
    if (!url) return;
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const can = (fn) => !!editor?.can().chain().focus()[fn]().run;

  return (
    <Box>
      {/* toolbar */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: 0.5,
        border: "1px solid", borderColor: "divider", borderBottom: "none",
        px: 0.5, py: 0.5, borderTopLeftRadius: 1, borderTopRightRadius: 1, bgcolor: "background.paper",
      }}>
        <Tooltip title="Bold"><span>
          <IconButton size="small" color={editor?.isActive("bold") ? "primary" : "default"}
            onClick={() => editor?.chain().focus().toggleBold().run()}
            disabled={!editor?.can().chain().focus().toggleBold().run()}>
            <FormatBold fontSize="small" />
          </IconButton>
        </span></Tooltip>
        <Tooltip title="Italic"><span>
          <IconButton size="small" color={editor?.isActive("italic") ? "primary" : "default"}
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            disabled={!editor?.can().chain().focus().toggleItalic().run()}>
            <FormatItalic fontSize="small" />
          </IconButton>
        </span></Tooltip>
        <Tooltip title="Underline"><span>
          <IconButton size="small" color={editor?.isActive("underline") ? "primary" : "default"}
            onClick={() => editor?.chain().focus().toggleUnderline().run()}
            disabled={!editor?.can().chain().focus().toggleUnderline().run()}>
            <FormatUnderlined fontSize="small" />
          </IconButton>
        </span></Tooltip>

        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        <Tooltip title="Bullet list"><span>
          <IconButton size="small" color={editor?.isActive("bulletList") ? "primary" : "default"}
            onClick={() => editor?.chain().focus().toggleBulletList().run()}>
            <FormatListBulleted fontSize="small" />
          </IconButton>
        </span></Tooltip>
        <Tooltip title="Ordered list"><span>
          <IconButton size="small" color={editor?.isActive("orderedList") ? "primary" : "default"}
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}>
            <FormatListNumbered fontSize="small" />
          </IconButton>
        </span></Tooltip>

        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        <Tooltip title={editor?.isActive("link") ? "Remove link" : "Add link"}><span>
          <IconButton size="small" onClick={setOrUnsetLink}>
            {editor?.isActive("link") ? <LinkOffIcon fontSize="small" /> : <LinkIcon fontSize="small" />}
          </IconButton>
        </span></Tooltip>

        <Box sx={{ flex: 1 }} />

        <Tooltip title="Undo"><span>
          <IconButton size="small" onClick={() => editor?.chain().focus().undo().run()} disabled={!can("undo")}>
            <Undo fontSize="small" />
          </IconButton>
        </span></Tooltip>
        <Tooltip title="Redo"><span>
          <IconButton size="small" onClick={() => editor?.chain().focus().redo().run()} disabled={!can("redo")}>
            <Redo fontSize="small" />
          </IconButton>
        </span></Tooltip>

        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />

        <Tooltip title="Clear formatting"><span>
          <IconButton size="small" onClick={() => editor?.chain().focus().clearNodes().unsetAllMarks().run()}>
            <ClearIcon fontSize="small" />
          </IconButton>
        </span></Tooltip>
      </Box>

      {/* editor area */}
      <Box sx={{
        border: "1px solid", borderColor: "divider",
        borderBottomLeftRadius: 1, borderBottomRightRadius: 1,
        "& .tiptap-editor": { minHeight, outline: "none", p: 1.5, fontSize: 14 },
      }}>
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}
